import {useState, useEffect} from 'react';
import {filter} from 'rxjs';
import {v4 as uuid} from 'uuid';
import {defineMessages, useIntl} from 'react-intl';
import {view, tokens} from '@sail/ui';
import {createKeyframes} from '@sail/engine';
import type {
  AgentCallResponse,
  Conversation,
  ConversationMessage,
} from '@stripe-internal/conversations-client';
import {useMessagesTopAnchored} from '../../../helpers/useMessagesTopAnchored';
import {ChatButtonOptions} from '../../../Chat/Row/ChatButtonOptions';

const rotateGradient = createKeyframes({
  0: {transform: 'rotate(0deg)'},
  100: {transform: 'rotate(360deg)'},
});

const fadeInOut = createKeyframes({
  0: {opacity: 0},
  100: {opacity: 0.6},
});

type PlanCardProps = {
  agentCallResponse: AgentCallResponse;
  conversation: Conversation;
  messageId: string;
};

const messages = defineMessages({
  yesGetStarted: {
    id: 'conversations_ui.agent_plan_card.yes_get_started',
    description:
      'The text that shows when hovering over the "Yes, get started" button',
    defaultMessage: 'Yes, get started',
  },
  cancelChanges: {
    id: 'conversations_ui.agent_plan_card.cancel_changes',
    description:
      'The text that shows when hovering over the "No, I need to make changes" button',
    defaultMessage: 'No, I need to make changes',
  },
});

function isAgentMessage(msg?: ConversationMessage) {
  return msg?.nonce?.startsWith('agent-');
}

export const PlanCard = ({
  agentCallResponse,
  conversation,
  messageId,
}: PlanCardProps) => {
  const intl = useIntl();
  const title = agentCallResponse.plan_short_title;
  const items = agentCallResponse.plan ?? [];
  const isLastMessage = conversation.mostRecentMessage()?.id === messageId;

  const {setTopAnchoringEnabled} = useMessagesTopAnchored(conversation);
  const [loading, setLoading] = useState(false);

  const [isAccepted, setIsAccepted] = useState(false);

  useEffect(() => {
    // We need to do two things:
    // 1. Check when the card is "loading" which is when the backend is responding
    //    to the agent message that we send. We assume a loading state if the agent
    //    message is the latest message
    // 2. Turn back on top anchoring when we are done
    let previousMessage: ConversationMessage | undefined;
    conversation
      .eventsObservable()
      .pipe(filter((e) => e.name === 'conversation-message-added'))
      .subscribe((e) => {
        if (isAgentMessage(previousMessage) && !isAgentMessage(e.message)) {
          // This timeout isn't great, but we need to flip this back on after
          // this message has rendered so the response to the agent message
          // doesn't take part in top anchoring.
          //
          // TODO: We should rethink how to allow experiences to turn this on/off
          setTimeout(() => {
            setTopAnchoringEnabled(true);
          }, 50);
          setLoading(false);
        } else if (isAgentMessage(e.message)) {
          setLoading(true);
        }

        previousMessage = e.message;
      });
  }, [conversation, setTopAnchoringEnabled]);

  const sendMessage = async (messageText: string) => {
    setTopAnchoringEnabled(false);
    const textMessage = conversation.buildTextMessage(messageText);
    await conversation.sendMessage({...textMessage, nonce: `agent-${uuid()}`});
  };

  const buttonOptions = [
    {
      value: 'accept',
      label: intl.formatMessage(messages.yesGetStarted),
      onPress: () => {
        sendMessage(intl.formatMessage(messages.yesGetStarted));
        setIsAccepted(true);
      },
    },
    {
      value: 'cancel',
      label: intl.formatMessage(messages.cancelChanges),
      onPress: () => sendMessage(intl.formatMessage(messages.cancelChanges)),
    },
  ];

  return (
    <>
      <view.div css={{position: 'relative', maxWidth: '400px'}}>
        {loading && isAccepted ? (
          <view.div
            css={{
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              filter: 'blur(4px)',
              borderRadius: 'xlarge',
            }}
          >
            <view.div
              css={{
                position: 'absolute',
                background:
                  'conic-gradient(in oklch shorter hue from 180deg at 50% 50%, #80E9FF 0deg, #7A73FF 90deg, #80E9FF 180deg, #7A73FF 270deg, #80E9FF 360deg);',
                width: '500%',
                height: '500%',
                borderRadius: 'large',
                animation: `${rotateGradient} 5s linear infinite, ${fadeInOut} 300ms ease-out`,
                opacity: 0.6,
                transformOrigin: 'center center',
              }}
            />
          </view.div>
        ) : null}
        <view.div
          css={{
            borderRadius: 'large',
            padding: 'space.75',
            stack: 'y',
            gap: 'small',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <view.div
            data-testid="plan-card"
            css={{
              stack: 'y',
              gap: 'medium',
              maxWidth: '500px',
              position: 'relative',
              borderRadius: 'large',
              padding: 'medium',
              borderStyle: 'solid',
              borderColor: tokens.color.neutral[50].toString(),
              borderWidth: 1,
              backgroundColor: 'surface',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <view.span css={{font: 'label.medium.emphasized'}}>
              {title}
            </view.span>
            {items.map((item, idx) => (
              <view.div
                css={{
                  stack: 'x',
                  gap: 'small',
                  alignItems: 'flex-start',
                }}
                key={item.uuid}
              >
                <view.span
                  css={{font: 'label.medium', color: 'subdued', flexShrink: 0}}
                >
                  {idx + 1}.
                </view.span>
                <view.span css={{font: 'label.medium', color: 'subdued'}}>
                  {item.step}
                </view.span>
              </view.div>
            ))}
          </view.div>
        </view.div>
      </view.div>
      {isLastMessage ? (
        <view.div css={{marginTop: 'large'}}>
          <ChatButtonOptions options={buttonOptions} animated />
        </view.div>
      ) : null}
    </>
  );
};
